const url = new URL(window.location.href);

const reloadPage = () => {
  url.searchParams.delete('page');
  window.location.href = url.toString();
};

(async () => {
  const eventsFilterElements = document.querySelectorAll<HTMLElement>(
    '.events-filters .events-filters__filter',
  );
  eventsFilterElements.forEach((eventsFilterElement) => {
    const labelElement =
      eventsFilterElement.querySelector<HTMLLabelElement>('.filter__label');
    if (!labelElement) return;

    eventsFilterElement.style.setProperty(
      '--label-width',
      `${labelElement.clientWidth}px`,
    );
  });

  const eventsFiltersLocationSelectElement =
    document.querySelector<HTMLSelectElement>(
      '.events-filters .events-filters__filter .filter__input.filter__input--location',
    );
  const eventsFiltersDateInputElement =
    document.querySelector<HTMLInputElement>(
      '.events-filters .events-filters__filter .filter__input.filter__input--date',
    );
  const eventsFiltersTypeSelectElement =
    document.querySelector<HTMLSelectElement>(
      '.events-filters .events-filters__filter .filter__input.filter__input--type',
    );

  // Registered before anything the date filter needs, and each on its own
  // element rather than behind one combined guard. Nothing submits these
  // selects: changing one does something only because the listener writes the
  // query parameter and reloads. So every way the calendar can give up — a
  // missing date input, the moment interop, a rejected dynamic import — used
  // to take location and type with it, while the comments below claimed it did
  // not.
  eventsFiltersLocationSelectElement?.addEventListener('input', (e) => {
    const newLocation = (e.currentTarget as HTMLSelectElement).value;
    if (!newLocation) {
      url.searchParams.delete('location');
    } else {
      url.searchParams.set('location', newLocation);
    }

    reloadPage();
  });
  eventsFiltersTypeSelectElement?.addEventListener('input', (e) => {
    const newType = (e.currentTarget as HTMLSelectElement).value;
    if (!newType) {
      url.searchParams.delete('type');
    } else {
      url.searchParams.set('type', newType);
    }

    reloadPage();
  });

  if (!eventsFiltersDateInputElement) {
    return;
  }

  // Not named `moment`, and not assumed to arrive the same way twice.
  //
  // vite.config.ts' wp_scripts() externalises this import onto WordPress' own
  // `moment` global — the one EventPostType enqueues on this archive — but not
  // identically in both modes. The build wraps it as a CJS module with a
  // `default`; the dev server rewrites the call to `(await
  // Promise.resolve(moment)).default` and hands back the bare global, where
  // `.default` is undefined. Reading it blind therefore worked in exactly one of
  // the two.
  //
  // The name matters as much: a destination called `moment` shadows that global
  // and reads itself in the temporal dead zone, which is the
  // `Cannot access 'moment' before initialization` the archive threw. The build
  // only hid that half by renaming the local binding.
  const momentModule = await import('moment');

  type MomentFactory = typeof momentModule.default;

  const momentLib: MomentFactory =
    typeof (momentModule as unknown) === 'function'
      ? (momentModule as unknown as MomentFactory)
      : momentModule.default;

  // Guarded because the interop above can still come up empty: a module that is
  // neither callable nor carries a `default` leaves `momentLib` undefined, and
  // assigning that would clobber the global `EventPostType` enqueues core's own
  // `moment` handle to provide — losing it for every other consumer on the page,
  // and throwing on the Lightpick call below. Bailing here costs the date filter
  // and nothing else, because location and type are already listening.
  if (typeof momentLib !== 'function') {
    // Logged rather than returned quietly: this is the one failure shape the
    // catch below cannot see, because the IIFE returns normally instead of
    // rejecting. Without a line here the date input just sits there readonly
    // and nothing anywhere says why — the same reason enroll-form.ts warns
    // when its lookup gives up.
    // eslint-disable-next-line no-console
    console.error(
      '[events-filters] the moment module arrived without a callable export, so the date filter stays off',
    );

    return;
  }

  // Lightpick reads the global rather than taking an argument.
  window.moment = momentLib;

  const Lightpick = (await import('lightpick')).default;

  let datepickerChanged = false;
  const datepicker = new Lightpick({
    field: eventsFiltersDateInputElement,
    singleDate: false,
    minDate: momentLib(),
    hoveringTooltip: false,
    footer: true,
    dropdowns: {
      years: {
        min: new Date().getFullYear(),
        max: new Date().getFullYear() + 1,
      },
      months: true,
    },
    orientation: 'bottom right',
    locale: {
      buttons: {
        prev: '←',
        next: '→',
        close: '×',
        reset: 'Herstellen',
        apply: 'Toepassen',
      },
      tooltip: {
        one: 'dag',
        other: 'dagen',
      },
    },
    onSelect: (startDate, endDate) => {
      if (startDate) {
        url.searchParams.set(
          'start_date',
          startDate
            .set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            .toISOString(),
        );
      }
      if (endDate) {
        url.searchParams.set(
          'end_date',
          endDate
            .set({ hours: 23, minutes: 59, seconds: 59, milliseconds: 0 })
            .toISOString(),
        );
      }

      if (startDate && endDate) {
        datepickerChanged = true;
      }

      window.history.pushState('', '', url);
    },
    onClose: () => {
      if (datepickerChanged) {
        reloadPage();
      }
    },
  });

  document
    .querySelector('.lightpick .lightpick__reset-action')
    ?.addEventListener('click', () => {
      url.searchParams.delete('start_date');
      url.searchParams.delete('end_date');
      reloadPage();
    });

  const queryStartDate = url.searchParams.get('start_date');
  const queryEndDate = url.searchParams.get('end_date');
  if (!queryStartDate || !queryEndDate) {
    eventsFiltersDateInputElement.value = 'Alle datums';
  } else {
    datepicker.setDateRange(momentLib(queryStartDate), momentLib(queryEndDate));
  }
})().catch((error: unknown) => {
  // The IIFE is fire-and-forget, so a rejected dynamic import — moment or
  // lightpick not delivered — used to vanish here and leave the date filter
  // silently inert. Location and type registered before any of that could
  // reject, so this must not take the page down; it only makes the reason the
  // calendar never opens findable.
  // eslint-disable-next-line no-console
  console.error('[events-filters] could not set up the filters:', error);
});
